import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useRequireAuth } from '@/hooks/use-require-auth';
import { previewJoin, joinEvent } from '@/lib/events-api';
import { ArrowLeft, Calendar, Tag, Clock, MapPin, Users, Lock } from 'lucide-react';

function formatDateRange(start, end) {
  if (!start) return '';
  const s = new Date(start);
  if (isNaN(s.getTime())) return start;
  const opts = { year: 'numeric', month: 'short', day: 'numeric' };
  const startStr = s.toLocaleDateString(undefined, opts);
  if (!end) return startStr;
  const e = new Date(end);
  if (isNaN(e.getTime())) return startStr;
  return `${startStr} — ${e.toLocaleDateString(undefined, opts)}`;
}

export default function EventPreviewPage() {
  const navigate = useNavigate();
  const [sp] = useSearchParams();
  const token = sp.get('token');
  const code = sp.get('event_id_code');
  const { ready } = useRequireAuth();

  const [event, setEvent] = useState(null);
  const [alreadyMember, setAlreadyMember] = useState(false);
  const [source, setSource] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [joining, setJoining] = useState(false);

  useEffect(() => {
    if (!ready) return;
    (async () => {
      setLoading(true); setError(null);
      try {
        const body = await previewJoin(token ? { invitation_token: token } : { event_id_code: code });
        setEvent(body.event); setAlreadyMember(body.already_member); setSource(body.join_source);
      } catch (e) { setError(e.message || "This code or invitation isn't valid."); }
      finally { setLoading(false); }
    })();
  }, [ready, token, code]);

  async function onJoin() {
    if (!event) return;
    setJoining(true); setError(null);
    try {
      await joinEvent(token ? { invitation_token: token } : { event_id_code: code });
      navigate(`/event-joined?event_id=${encodeURIComponent(event.id)}`, { replace: true });
    } catch (e) { setError(e.message || 'Join failed.'); }
    finally { setJoining(false); }
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center p-4" data-testid="event-preview-screen">
      <div className="w-full max-w-md">
        <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-[#146BFF] font-semibold text-sm mb-4 hover:underline" data-testid="preview-back-btn">
          <ArrowLeft className="w-4 h-4" /> back
        </button>
        <div className="mb-1 flex items-center gap-2"><img src="/allviews-mark.svg" alt="" className="h-5 w-5" /><img src="/allviews-wordmark.svg" alt="ALLVIEWS" className="h-3.5" /></div>
        <h1 className="text-2xl font-extrabold text-[#0B1B46] mt-4 mb-1" data-testid="preview-title">Event Preview</h1>
        <p className="text-sm text-[#5B6472] mb-4">Confirm this is the event you were invited to.</p>

        {loading ? (
          <div className="flex justify-center py-8" data-testid="preview-loading">
            <div className="w-8 h-8 border-2 border-[#146BFF] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : error ? (
          <div className="bg-[#FBE5E5] border border-[#C93434]/20 rounded-xl p-4" data-testid="preview-error">
            <p className="font-bold text-[#C93434] mb-1">Not a valid event</p>
            <p className="text-sm text-[#C93434] mb-3">{error}</p>
            <button onClick={() => navigate('/join', { replace: true })}
              className="w-full h-10 bg-white border border-[#E3E8EF] text-[#0B1B46] font-semibold rounded-lg hover:bg-[#F3F5F8] transition-colors"
              data-testid="preview-retry-btn">Try a different code</button>
          </div>
        ) : event ? (
          <>
            <div className="bg-white border border-[#E3E8EF] rounded-xl p-4 mb-4" data-testid="preview-event-card">
              <div className="flex items-center gap-2 mb-3">
                <Calendar className="w-4 h-4 text-[#146BFF]" />
                <span className="text-[10px] font-extrabold text-[#146BFF] tracking-widest">YOU'RE INVITED TO</span>
              </div>
              <h2 className="text-xl font-extrabold text-[#0B1B46] mb-3" data-testid="preview-event-name">{event.name}</h2>
              <div className="flex flex-col gap-1.5 text-sm text-[#5B6472]">
                <div className="flex items-center gap-2"><Tag className="w-3.5 h-3.5" />{event.category}</div>
                {event.start_at && <div className="flex items-center gap-2"><Clock className="w-3.5 h-3.5" /><span data-testid="preview-event-date">{formatDateRange(event.start_at, event.end_at)}</span></div>}
                {event.venue_name && <div className="flex items-center gap-2"><MapPin className="w-3.5 h-3.5" /><span data-testid="preview-event-venue">{event.venue_name}{event.venue_address ? `, ${event.venue_address}` : ''}</span></div>}
                <div className="flex items-center gap-2"><Users className="w-3.5 h-3.5" /><span data-testid="preview-contributor-count">{event.contributor_count ?? 0} contributor{(event.contributor_count ?? 0) === 1 ? '' : 's'}</span></div>
              </div>
              <div className="flex flex-wrap gap-2 mt-3">
                <span className={`px-2.5 py-0.5 rounded-full text-xs font-bold ${event.status === 'COLLECTING' ? 'bg-[#E4F4EC] text-[#1F8A55]' : 'bg-[#EFF5FF] text-[#146BFF]'}`} data-testid="preview-event-status">{event.status}</span>
                <span className="px-2.5 py-0.5 rounded-full text-xs font-bold bg-[#EFF5FF] text-[#146BFF]" data-testid="preview-source-badge">
                  {source === 'invitation_token' ? 'Invite link' : 'Event ID'}
                </span>
                {alreadyMember && <span className="px-2.5 py-0.5 rounded-full text-xs font-bold bg-[#E4F4EC] text-[#1F8A55]" data-testid="preview-already-member">Already joined</span>}
              </div>
            </div>

            <div className="bg-[#EFF5FF] border border-[#D6E4FF] rounded-xl p-3 flex items-start gap-2 mb-4" data-testid="preview-privacy-banner">
              <Lock className="w-4 h-4 text-[#146BFF] mt-0.5 flex-shrink-0" />
              <p className="text-sm text-[#5B6472]">Your uploads are private. Only you and the host can see them until the host launches a Lookbook.</p>
            </div>

            <button onClick={onJoin} disabled={joining}
              className="w-full h-11 bg-[#146BFF] hover:bg-[#0F55CC] disabled:opacity-50 text-white font-semibold rounded-lg mb-2 transition-colors focus:outline-none focus:ring-2 focus:ring-[#146BFF] focus:ring-offset-2"
              data-testid="preview-join-btn">
              {joining ? 'Joining…' : alreadyMember ? 'Continue to event' : 'Join Event'}
            </button>
            <button onClick={() => navigate('/', { replace: true })}
              className="w-full h-11 text-[#5B6472] font-semibold rounded-lg hover:bg-[#F3F5F8] transition-colors"
              data-testid="preview-cancel-btn">Cancel</button>
          </>
        ) : null}
      </div>
    </div>
  );
}
